import { match } from 'ts-pattern';

import { useApiClient } from '@eluve/api-client-provider';
import { combineSummaryWithNotes } from '@eluve/frontend-feature-ehr-sync';
import { SupportedVendors } from '@eluve/graphql-types';
import { ClassicSummary, DynamicSummary } from '@eluve/llm-outputs';
import {
  HumanFriendlyError,
  MessageResponse,
  SyncToEhr,
  getHumanFriendlyError,
} from '@eluve/vendor';

type SyncToEhrArgs = {
  tenantId: string;
  appointmentId: string;
  vendor: SupportedVendors;
  domain: string;
  patientExternalId: string;
  appointmentName: string;
  additionalUserNotes?: string | null;
  shouldSignExternalChart: boolean;
  appointmentExternalId?: string;
  summary: ClassicSummary | DynamicSummary;
  templateExternalId?: string;
  appointmentStartDate: string;
};

export type SyncToEhrResponse = MessageResponse<{
  chartUrl: string;
  chartId: string;
}> & { humanFriendlyError?: HumanFriendlyError };

const MISSING_MESSAGE_CLIENT_ERROR: SyncToEhrResponse = {
  ok: false,
  error: 'Extension integration not available for this EHR vendor',
  humanFriendlyError: {
    title: 'EHR Integration Not Available',
    message:
      'This EHR vendor requires browser extension integration which is not available in this context.',
  },
};

export type UseSyncToEhrParams =
  | {
      apiClientOnly?: false;
      syncToEhr: SyncToEhr;
    }
  | { apiClientOnly: true };

export const useSyncToEhr = (params: UseSyncToEhrParams) => {
  const syncToEhr = params.apiClientOnly ? undefined : params.syncToEhr;
  const api = useApiClient();

  return async (args: SyncToEhrArgs): Promise<SyncToEhrResponse> => {
    const {
      appointmentId,
      vendor,
      domain,
      patientExternalId,
      appointmentName,
      additionalUserNotes,
      shouldSignExternalChart,
      summary,
      appointmentExternalId,
      tenantId,
      templateExternalId,
      appointmentStartDate,
    } = args;

    const summaryData =
      summary.type === 'SOAP' && summary.data ? summary.data : summary;

    const response = await match(vendor)
      .with('JANE', async (vendor) => {
        if (!syncToEhr) {
          return MISSING_MESSAGE_CLIENT_ERROR;
        }
        const response = await syncToEhr({
          vendor,
          domain,
          data: {
            sign: shouldSignExternalChart,
            summary: combineSummaryWithNotes(summaryData, additionalUserNotes),
            patientExternalId: patientExternalId!,
            description: appointmentName,
            appointmentExternalId,
            templateExternalId,
            appointmentStartDate,
          },
        });

        if (!response.ok || !response.data) {
          const humanFriendlyError = getHumanFriendlyError(response.error);
          return { ...response, humanFriendlyError };
        }
        return response;
      })
      .with('TEBRA', 'MOVEMENT_X', async (vendor) => {
        if (!syncToEhr) {
          return MISSING_MESSAGE_CLIENT_ERROR;
        }
        const response = await syncToEhr({
          vendor,
          domain,
          data: {
            sign: shouldSignExternalChart,
            summary: combineSummaryWithNotes(summaryData, additionalUserNotes),
            patientExternalId: patientExternalId!,
            description: appointmentName,
            appointmentExternalId,
            appointmentStartDate,
          },
        });

        if (!response.ok || !response.data) {
          const humanFriendlyError = getHumanFriendlyError(response.error);
          return { ...response, humanFriendlyError };
        }
        return response;
      })
      .with('SILOAM', async (vendor) => {
        const isEluveDomain =
          typeof window !== 'undefined' &&
          (window.location.hostname.includes('.eluve.com') ||
            window.location.hostname.includes('eluve.dev'));

        // On eluve domain, call API directly
        // On Siloam SOAP page (extension context), use extension messaging for DOM manipulation
        if (!isEluveDomain && syncToEhr) {
          const response = await syncToEhr({
            vendor,
            domain,
            data: {
              tenantId,
              appointmentId,
              sign: shouldSignExternalChart,
              summary: combineSummaryWithNotes(
                summaryData,
                additionalUserNotes,
              ),
              patientExternalId: patientExternalId!,
              description: appointmentName,
              appointmentExternalId,
              appointmentStartDate,
            },
          });

          if (!response.ok || !response.data) {
            const humanFriendlyError = getHumanFriendlyError(response.error);
            return { ...response, humanFriendlyError };
          }
          return response;
        }

        const response = await api.appointments.syncToEhr({
          body: {
            domain,
            vendor,
          },
          params: { tenantId, appointmentId },
        });
        if (response.status === 201) {
          return {
            ok: true,
            data: {
              chartUrl: response.body.chartUrl,
              chartId: response.body.chartId,
            },
          };
        } else if (response.status === 422) {
          const errorMessage = response.body.error;
          return {
            ok: false,
            error: errorMessage,
            humanFriendlyError: {
              title: errorMessage,
              message: '',
            },
          };
        }
        return {
          ok: false,
          error: `Could not sync to EHR for ${vendor} ${domain}`,
        };
      })
      .with('EXPERITY', async (vendor) => {
        if (!syncToEhr) {
          return MISSING_MESSAGE_CLIENT_ERROR;
        }
        const pdfResult = await api.appointments.getOrCreatePdfUrl({
          params: {
            appointmentId,
            tenantId,
          },
          body: {
            type: 'CHART',
          },
        });

        if (pdfResult.status !== 201) {
          throw new Error('Failed to get PDF URL for appointment');
        }

        return syncToEhr({
          tenantId,
          vendor,
          domain,
          data: {
            patientExternalId: patientExternalId!,
            appointmentExternalId,
            category: 'Transcription',
            tenant: 'CRHGA',
            description: appointmentName,
            pdfUrl: pdfResult.body.pdfUrl,
          },
        });
      })
      .with('BLVD', async (vendor) => {
        const response = await api.appointments.syncToEhr({
          body: {
            domain,
            vendor,
          },
          params: { tenantId, appointmentId },
        });
        if (response.status === 201) {
          return {
            ok: true,
            data: {
              chartUrl: response.body.chartUrl,
              chartId: response.body.chartId,
            },
          };
        } else if (response.status === 422) {
          const errorMessage = response.body.error;
          return {
            ok: false,
            error: errorMessage,
            humanFriendlyError: {
              title: errorMessage,
              message: '',
            },
          };
        }
        return {
          ok: false,
          error: `Could not sync to EHR for ${vendor} ${domain}`,
        };
      })
      .exhaustive();
    return response;
  };
};
